import { useUtilityContext } from '@change-corgi/core-react/utilityContext';

import { useCountryCode } from 'src/app/shared/hooks/l10n';
import { useUpdatePerimeterXUserData } from 'src/app/shared/hooks/perimeterX';
import { useRefreshSession } from 'src/app/shared/hooks/session';

import { useInformVerificationEmail } from '../useInformVerificationEmail';
import { useLoginWithModal } from '../useLoginWithModal';

import { createUser as createUserApi } from './api';
import { UserAlreadyExistsError } from './UserAlreadyExistError';

type Props = {
	firstName: string;
	lastName: string;
	email: string;
	phoneNumber?: string;
	signupContext: string;
	path: string;
	googleAuthEnabled: boolean;
};

type Result = { success: true } | { success: false; isUserAlreadyExistsError: boolean };

export function useCreateUser(): (props: Props) => Promise<Result> {
	const utilityContext = useUtilityContext();
	const countryCode = useCountryCode();
	const refreshSession = useRefreshSession();
	const updatePerimeterXUserData = useUpdatePerimeterXUserData();
	const login = useLoginWithModal();
	const informVerificationEmail = useInformVerificationEmail();

	return async function createUser({
		firstName,
		email,
		lastName,
		signupContext,
		googleAuthEnabled,
		phoneNumber,
		path,
	}): Promise<Result> {
		try {
			const result = await createUserApi(
				{
					firstName,
					lastName,
					email,
					countryCode,
					signupMethod: 'PAYMENT',
					signupContext,
					...(phoneNumber ? { phoneNumber } : {}),
				},
				utilityContext,
			);
			if (typeof result === 'object') {
				await updatePerimeterXUserData();
				await refreshSession();
			}
			return { success: true };
		} catch (error) {
			if (!(error instanceof UserAlreadyExistsError)) return { success: false, isUserAlreadyExistsError: false };

			if (error.userPasswordSet) {
				void login({ email, showContinueAsGuest: false, googleAuthEnabled });
			} else {
				void informVerificationEmail({ showContinueAsGuest: false, userId: error.userId, path });
			}
			return { success: false, isUserAlreadyExistsError: true };
		}
	};
}
